/**
 * BottomNav — mobile-only persistent tab bar.
 * Renders on screens < md (768px), sits above OS gesture bar.
 *
 * Apr 2026 — redesigned to a 4-tab "real mobile app" layout per
 * user request (matching the Zen Calendar bottom-nav feel):
 *   Home (Dashboard) | Schedule | Chat | More
 *
 * "More" opens the existing left-side drawer which already contains
 * everything else (Players, Seasons, Stats, Finance, Notification
 * Log, Team Settings, Profile, Notifications, Logout). This keeps
 * the persistent tab bar small and uncluttered while still giving
 * one-tap access to every function in the app.
 *
 * Notifications + Profile are intentionally NOT top-level tabs —
 * they're niche enough to live behind the More menu, and we want
 * the four primary tabs to match the most common user flows
 * (open the app → check schedule or chat).
 */
import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, Calendar, MessageSquare, Menu } from 'lucide-react';

export function BottomNav({ activeTeam, unreadCount, unreadNotifs, onMoreClick }) {
  const location = useLocation();

  // 2026-08-08 (mobile cut-off report) — PUBLISH THE BAR'S REAL HEIGHT.
  // The scroll containers in MobileLayout/CapacitorLayout used to pad
  // their bottom by a HARDCODED guess ("the bar's own height is roughly
  // 54px"). Any change to icon size, label font, or safe-area inset
  // silently broke that assumption and the last row of content — on the
  // game page, the I'm In / Waitlist / I'm Out buttons — ended up
  // underneath the bar with no way to reach it.
  //
  // Measuring the rendered bar and writing it to --bottom-nav-h means
  // the padding can never drift from reality again. ResizeObserver
  // catches safe-area changes (rotation, iOS toolbar transitions) and
  // any future restyle of the bar.
  const navRef = useRef(null);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const publish = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty('--bottom-nav-h', `${h}px`);
    };
    publish();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(publish);
      ro.observe(el);
    }
    window.addEventListener('orientationchange', publish, { passive: true });
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('orientationchange', publish);
      // Leave the variable set on unmount — layouts that still reference
      // it during an unmount/remount cycle keep a sane value instead of
      // falling back mid-transition.
    };
  }, []);

  // Apr 2026 — when the user has no active team yet (first login,
  // or just finished onboarding before joining a team), the
  // Schedule and Chat tabs would lead nowhere. We still render
  // them disabled so the bar's geometry stays the same — empty
  // tabs swapping in/out feels unstable. Disabled tabs route to
  // /dashboard so any accidental tap doesn't 404.
  const fallback = '/dashboard';

  const tabs = [
    {
      to: '/dashboard',
      icon: LayoutDashboard,
      label: 'Home',
      end: true,
    },
    {
      to: activeTeam ? `/team/${activeTeam.id}/games` : fallback,
      icon: Calendar,
      label: 'Schedule',
      disabled: !activeTeam,
    },
    {
      to: activeTeam ? `/team/${activeTeam.id}/chat` : fallback,
      icon: MessageSquare,
      label: 'Chat',
      badge: unreadCount,
      disabled: !activeTeam,
    },
    {
      // "More" opens the side drawer — handled by the layout via
      // onMoreClick callback so the drawer's open state stays
      // owned by the layout (single source of truth).
      // Apr 2026 — show unread notification count as the "More"
      // badge so users still see at-a-glance that there's a new
      // alert (the bell lives inside the drawer now).
      onClick: onMoreClick,
      icon: Menu,
      label: 'More',
      badge: unreadNotifs,
    },
  ];

  return (
    // Apr 2026 — no `md:hidden` here. The component is only mounted from
    // MobileLayout / CapacitorLayout, both of which AppLayout selects
    // based on UA-driven `isMobilePhone` / `isCapacitor`. A Tailwind
    // breakpoint class would have hidden the bar in landscape on tall
    // phones (iPhone 15 Pro Max landscape = 932px wide → triggers md),
    // breaking the primary navigation. Layout selection is the right
    // gate; this is just the bar's visual definition.
    <nav ref={navRef}
      // 2026-08-08 — data-tab-bar added. index.css lifts
      // `.keyboard-open [data-tab-bar]` by --keyboard-h so the bar rides
      // above the on-screen keyboard. CapacitorLayout's bar matched that
      // selector; THIS bar (the one mobile web actually renders) never
      // did, so on the web the keyboard covered the tab bar.
      data-tab-bar
      style={{
      position: 'fixed', bottom: 0, left: 0, right: 0,
      zIndex: 400,
      background: 'rgba(6,10,15,0.96)',
      backdropFilter: 'blur(24px) saturate(1.6)',
      borderTop: '1px solid rgba(255,255,255,0.06)',
      boxShadow: '0 -1px 0 rgba(0,212,255,0.06), 0 -8px 40px rgba(0,0,0,0.4)',
      // Safe area inset for iPhone home indicator
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      display: 'flex',
      alignItems: 'stretch',
    }}>
      {tabs.map((tab, i) => {
        // Active state — for nav tabs, derive from URL. The "More"
        // tab never appears active (it's a momentary drawer trigger,
        // not a destination).
        // Apr 30 2026 — bug fix: disabled tabs MUST NOT show as
        // active. Without this guard, when the user is on /dashboard
        // and has no active team, the disabled Schedule/Chat tabs
        // (whose to= falls back to /dashboard) would all light up
        // as "active" alongside Home, completely confusing the
        // active-tab indicator. Requiring !tab.disabled fixes it.
        const isActive = tab.to && !tab.disabled && (
          tab.end
            ? location.pathname === tab.to
            : location.pathname.startsWith(tab.to)
        );
        const dim = tab.disabled ? 0.25 : (isActive ? 1 : 0.55);
        const color = isActive ? 'var(--ice)' : `rgba(255,255,255,${dim})`;

        const inner = (
          <span style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 3, padding: '10px 0 8px', position: 'relative', width: '100%',
          }}>
            {/* Active indicator dash above the icon */}
            {isActive && (
              <span style={{
                position: 'absolute', top: 6, width: 24, height: 2, borderRadius: 1,
                background: 'var(--ice)',
                boxShadow: '0 0 8px var(--ice)',
              }} />
            )}

            {/* Icon with optional unread badge */}
            <span style={{ position: 'relative' }}>
              <tab.icon size={20} color={color} strokeWidth={isActive ? 2.2 : 1.8} />
              {tab.badge > 0 && (
                <span style={{
                  position: 'absolute', top: -4, right: -5,
                  minWidth: 14, height: 14, padding: '0 3px',
                  borderRadius: 7, background: 'var(--penalty)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 9, fontWeight: 800, color: '#fff',
                  lineHeight: 1,
                  boxShadow: '0 0 6px rgba(255,59,59,0.6)',
                }}>
                  {tab.badge > 9 ? '9+' : tab.badge}
                </span>
              )}
            </span>

            {/* Label — Apr 2026: nowrap + ellipsis so accessibility
                text scaling (or unusually long future labels) don't
                wrap and grow the bar's height, which would push the
                chat composer under it. Truncation is acceptable —
                the icon already conveys the destination. */}
            <span style={{
              fontSize: 10, fontWeight: isActive ? 700 : 500,
              fontFamily: 'var(--font-display)',
              letterSpacing: isActive ? '0.08em' : '0.04em',
              textTransform: 'uppercase',
              color,
              transition: 'color 0.15s',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}>{tab.label}</span>
          </span>
        );

        const shared = {
          key: i,
          style: {
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'transparent', border: 'none',
            cursor: tab.disabled ? 'default' : 'pointer',
            WebkitTapHighlightColor: 'transparent',
            transition: 'transform 0.1s',
            opacity: tab.disabled ? 0.7 : 1,
            // Apr 2026 — pointer-events:none on disabled tabs so even
            // accidental taps near the edge don't trigger navigation.
            pointerEvents: tab.disabled ? 'none' : 'auto',
          },
          onTouchStart: e => {
            if (!tab.disabled) e.currentTarget.style.transform = 'scale(0.9)';
          },
          onTouchEnd: e => { e.currentTarget.style.transform = 'scale(1)'; },
        };

        if (tab.onClick) {
          return <button {...shared} onClick={tab.onClick}>{inner}</button>;
        }

        return (
          <NavLink {...shared} to={tab.to} end={tab.end}>
            {inner}
          </NavLink>
        );
      })}
    </nav>
  );
}
